import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import iconFiles from '../../assets/img/icon-files.svg';
import iconPages from '../../assets/img/icon-pages.svg';
import { loadSearchResultFromStorage } from '../../store/insuranceSearchStore';
import { getInsuranceDocumentViewUrl, fetchPdfDocument } from '../../services/api/InsuranceService';
import { trackGoodFeedback, trackBadFeedback } from '../../services/api/TrackingService';
import FeedbackPopup from '../../components/popups/FeedbackPopup';
import BadReasonFeedbackPopup from '../../components/popups/BadReasonFeedbackPopup';
import { usePdfKeywordSearch } from '../../hooks/usePdfKeywordSearch';
import PdfSearchPanel from '../../components/PdfSearchPanel';
import '../../styles/InsuranceSystemDetail.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { PDF_WIDTH_LANDSCAPE, PDF_WIDTH_PORTAIT } from '../../utils/insuranceSearchUtil';

// PDF.js Workerの設定
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// バージョン不一致警告を抑制
const originalConsoleWarn = console.warn;
console.warn = (...args) => {
  const message = args[0];
  if (typeof message === 'string' && 
      (message.includes('API version') || 
       message.includes('Worker version') || 
       message.includes('does not match'))) {
    // バージョン不一致警告をスキップ
    return;
  }
  originalConsoleWarn.apply(console, args);
};

const SESSION_STORAGE_KEY = 'insuranceSystemDetailSelectedResult';

const InsuranceSystemDetail = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [showErrorPage, setShowErrorPage] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [isFeedbackSubmitting, setIsFeedbackSubmitting] = useState(false);
  const [blobName, setBlobName] = useState(''); 
  const containerRef = useRef(null);
  const defaultDocumentTitleRef = useRef(document.title);
  const [summary, setSummary] = useState('');
  const [detailResult, setDetailResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  const [isLandscape, setIsLandscape] = useState(false);
  const [isRendering, setIsRendering] = useState(false);

  // キーワード検索用: pdfDocument は onDocumentLoadSuccess で設定し hook に渡す
  const [pdfDocument, setPdfDocument] = useState(null);
  // モバイル用検索パネルの開閉状態（UI状態のため検索ロジックフックには持たせない）
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // 新しく追加する状態
  const [documentInfo, setDocumentInfo] = useState({
    insuranceCompany: '',
    productName: '',
    date: '',
    relatedPage: ''
  });
  const [isDocInfoAccordionOpen, setIsDocInfoAccordionOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // フィードバックポップアップの状態管理
  const [currentPopup, setCurrentPopup] = useState('feedback');
  const [currentQueryId, setCurrentQueryId] = useState(null);

  // キーワード検索のカスタムフック
  const {
    searchText,
    setSearchText,
    searchResults,
    currentSearchIndex,
    isSearching,
    searchError,
    handleSearch,
    handleNextSearchResult,
    handlePrevSearchResult,
    handleClearSearch,
    customTextRenderer,
    onGetTextSuccess,
    onTextLayerRenderSuccess,
  } = usePdfKeywordSearch({ pdfDocument, currentPage, setCurrentPage, containerRef });

  // キーワード検索コントロールに渡すprops
  const searchControlProps = {
    searchText,
    onSearchTextChange: setSearchText,
    searchResults,
    currentSearchIndex,
    isSearching,
    onSearch: handleSearch,
    onNextSearchResult: handleNextSearchResult,
    onPrevSearchResult: handlePrevSearchResult,
    onClearSearch: handleClearSearch,
    pdfDocument,
    isMobile,
    searchError,
  };


  // 保険会社名に基づきタブタイトルを設定する
  const updateDocumentTitle = () => {
    const defaultTitle = defaultDocumentTitleRef.current || 'ASFiNDER';
    const companyName = documentInfo.insuranceCompany 
      ? String(documentInfo.insuranceCompany).trim() 
      : '';
    const displayName = companyName || '保険会社不明';

    document.title = `${displayName} | ${defaultTitle}`;

    return () => {
      document.title = defaultTitle;
    };
  };

  useEffect(updateDocumentTitle, [documentInfo.insuranceCompany]);

  // react-pdfの設定オプション（自動バージョン同期）
  const pdfOptions = useMemo(() => ({
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
  }), []);

  // レスポンシブ対応のためのリサイズ監視
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    handleResize(); // 初期値設定
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 閉じるボタンのハンドラー
  const handleClose = () => {
    // 現在のタブを閉じる
    window.close();
  };

  // フィードバックポップアップのハンドラー関数
  const handleFeedbackClose = () => {
    setCurrentPopup(null);
  };

  const handleGoodFeedback = async () => {
    if (isFeedbackSubmitting) return;
    
    setIsFeedbackSubmitting(true);
    
    try {
      await trackGoodFeedback(currentQueryId, '検索結果詳細');
      setCurrentPopup(null);
    } catch (error) {
      console.error('フィードバック送信エラー:', error);
    } finally {
      setIsFeedbackSubmitting(false);
    }
  };

  const handleBadFeedback = () => {
    if (isFeedbackSubmitting) return;
    setCurrentPopup('badReason');
  };

  const handleFeedbackSubmit = async (feedbackData) => {
    if (isFeedbackSubmitting) return;
    
    setIsFeedbackSubmitting(true);
    
    try {
      await trackBadFeedback(currentQueryId, '検索結果詳細', feedbackData);
    } catch (error) {
      console.error('フィードバック送信エラー:', error);
    } finally {
      setIsFeedbackSubmitting(false);
    }
  };

  const handleError = (errorMessage, error = null) => {
    const errorDetail = error ? `: ${error.message || '不明なエラー'}` : '';
    setErrorMessage(`${errorMessage}${errorDetail}`);
    setShowErrorPage(true);
    setLoading(false);
  };

  const loadResultFromSessionStorage = () => {
    if (typeof window === 'undefined') return null;

    try {
      const storedValue = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
      return storedValue ? JSON.parse(storedValue) : null;
    } catch (error) {
      console.error('sessionStorageからの読み込みに失敗しました:', error);
      try {
        window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
      } catch (removeError) {
        console.error('sessionStorageのクリアに失敗しました:', removeError);
      }
      return null;
    }
  };

  const saveResultToSessionStorage = (resultData) => {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(resultData));
    } catch (error) {
      console.error('sessionStorageへの保存に失敗しました:', error);
    }
  };

  const initializeDetailFromResult = (resultData, { persistSession } = { persistSession: false }) => {
    if (!resultData) {
      return false;
    }

    if (persistSession) {
      saveResultToSessionStorage(resultData);
    }

    setDetailResult(resultData);

    if (resultData.queryId) {
      setCurrentQueryId(resultData.queryId);
    }

    processSearchResult(resultData);

    return true;
  };

  useEffect(() => {
    // バージョン不一致警告を一時的に抑制
    const originalConsoleWarn = console.warn;
    console.warn = function(message) {
      if (typeof message === 'string' && message.includes('version') && message.includes('match')) {
        // バージョン不一致警告をスキップ
        return;
      }
      originalConsoleWarn.apply(console, arguments);
    };
    
    try {
      const sessionResult = loadResultFromSessionStorage();
      let initialized = false;

      if (sessionResult) {
        initialized = initializeDetailFromResult(sessionResult, { persistSession: false });
      }

      if (!initialized) {
        const storedResult = loadSearchResultFromStorage();

        if (storedResult) {
          initialized = initializeDetailFromResult(storedResult, { persistSession: true });
        }
      }

      if (!initialized) {
        handleError('検索結果データが見つかりません。検索画面から選択してください。');
      }
    } catch (error) {
      alert('初期化処理中にエラーが発生しました。');
      handleError('初期化処理中にエラーが発生しました', error);
    }

    // クリーンアップでconsole.warnを元に戻す
    return () => {
      console.warn = originalConsoleWarn;
    };
  }, []);

  const extractYearMonth = (isoDateString) => {
    if (!isoDateString) return null;

    try {
      const date = new Date(isoDateString);
      
      // 有効な日付かチェック
      if (isNaN(date.getTime())) {
        console.error('無効な日付形式:', isoDateString);
        return null;
      }

      const year = date.getFullYear();
      const month = date.getMonth() + 1; // 0から始まるので+1
      
      return `${year}年${month.toString().padStart(2, '0')}月`;
    } catch (error) {
      console.error('日付の解析に失敗:', error);
      return null;
    }
  };
  
  const processSearchResult = (resultData) => {
    try {
      if (resultData.pdfUrl) {
        const normalizedPath = resultData.pdfUrl.replace(/\\/g, '/');
        setBlobName(normalizedPath);
      } else {
        handleError('PDFのURLがありません');
        return;
      }
      
      if (resultData.summary) {
        setSummary(resultData.summary);
      }

      const yearMonth = extractYearMonth(resultData.creationDate);

      // 文書情報を設定
      setDocumentInfo({
        insuranceCompany: resultData.companyName,
        productName: resultData.productName,
        date: yearMonth,
        relatedPage: resultData.pages
      });

      // 初期ページ設定
      if (resultData.pages) {
        setCurrentPage(resultData.pages);
      }
    } catch (error) {
      alert('検索結果データの処理中にエラーが発生しました。');
      handleError('検索結果データの処理中にエラーが発生しました', error);
    }
  };

  const loadPdfDocument = async () => {
    if (!blobName || !detailResult) {
      return;
    }

    try {
      setLoading(true);
      
      const documentUrl = getInsuranceDocumentViewUrl(blobName);
      
      if (!documentUrl) {
        throw new Error('PDFのURLが無効です');
      }

      const params = detailResult.insuranceType
        ? { insurance_type: detailResult.insuranceType }
        : {};
      const fetchPromise = fetchPdfDocument(documentUrl, params);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('PDFの取得がタイムアウトしました')), 30000)
      );
      
      const arrayBuffer = await Promise.race([fetchPromise, timeoutPromise]);
      
      if (!arrayBuffer) {
        throw new Error('APIからの応答がありません');
      }
      
      if (arrayBuffer.byteLength === 0) {
        throw new Error('PDFデータが空です');
      }

      // ArrayBufferを直接設定（元のソースの方式に合わせる）
      setPdfFile(arrayBuffer);
      setShowErrorPage(false);
      setLoading(false);
      setIsRendering(true); // 初回ページレンダリング開始
    } catch (error) {
      // エラーダイアログ表示はBaseApiServiceで行う
      // エラーコンソール出力は各ApiServiceで行う
      handleError('PDFビューワー設定エラー', error);
    }
  };

  useEffect(() => {
    if (blobName && detailResult) {
      loadPdfDocument();
    }
  }, [blobName, detailResult]);

  // PDF読み込み成功時のハンドラー
  const onDocumentLoadSuccess = (pdf) => {
    setTotalPages(pdf.numPages);
    setPdfDocument(pdf);
    setLoading(false);
    setShowErrorPage(false);
  };

  // PDF読み込みエラー時のハンドラー
  const onDocumentLoadError = (error) => {
    handleError('PDFの読み込みに失敗しました', error);
  };

  // ページ読み込み成功時のハンドラー
  const onPageLoadSuccess = async (page) => {
    setIsRendering(false); // レンダリング完了
    
    // ページの向きを判定
    const viewport = page.getViewport({ scale: 1 });
    const isPageLandscape = viewport.width > viewport.height;
    setIsLandscape(isPageLandscape);

    try {
      // テキスト抽出（将来の機能拡張用）
      const textContent = await page.getTextContent();
    } catch (err) {
      // テキスト抽出エラーは無視
    }
  };

  // ページ読み込みエラー時のハンドラー
  const onPageLoadError = (error) => {
    setErrorMessage(`ページ ${currentPage} の読み込みに失敗しました: ${error.message}`);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 10, 200));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 10, 50));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setIsRendering(true); // ページ変更開始
      setCurrentPage(newPage);
    }
  };

  const handlePrevPage = () => {
    if (!isRendering) { // レンダリング中は無効
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (!isRendering) { // レンダリング中は無効
      handlePageChange(currentPage + 1);
    }
  };

  const handleRetry = () => {
    setLoading(true);
    setShowErrorPage(false);
    setErrorMessage('');
    setPdfFile(null);
    setTotalPages(0);
    setCurrentPage(1);
    setIsRendering(false); // レンダリング状態リセット
    
    // 少し遅延させてから再読み込み
    setTimeout(() => {
      loadPdfDocument();
    }, 100);
  };

  // ドラッグ&パン機能
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX,
      y: e.clientY
    });
    e.preventDefault();
    document.body.style.userSelect = 'none';
  };

  const handleMouseMove = (e) => {
    if (isDragging && containerRef.current) {
      const deltaX = dragStart.x - e.clientX;
      const deltaY = dragStart.y - e.clientY;
      
      containerRef.current.scrollLeft = scrollPosition.x + deltaX;
      containerRef.current.scrollTop = scrollPosition.y + deltaY;
      
      e.preventDefault();
    }
  };

  const handleMouseUp = () => {
    if (isDragging && containerRef.current) {
      setScrollPosition({
        x: containerRef.current.scrollLeft,
        y: containerRef.current.scrollTop
      });
    }
    setIsDragging(false);
    document.body.style.userSelect = '';
  };

  const handleMouseLeave = () => {
    if (isDragging && containerRef.current) {
      setScrollPosition({
        x: containerRef.current.scrollLeft,
        y: containerRef.current.scrollTop
      });
    }
    setIsDragging(false);
    document.body.style.userSelect = '';
  };

  const handleBackToSearch = () => {
    navigate('/solicitor/insurance/system/search');
  };

  // アコーディオンの開閉ハンドラー
  const toggleDocInfoAccordion = () => {
    setIsDocInfoAccordionOpen(!isDocInfoAccordionOpen);
  };

  // ズームリセット時のスクロール位置リセット
  useEffect(() => {
    if (containerRef.current && zoomLevel === 100) {
      containerRef.current.scrollLeft = 0;
      containerRef.current.scrollTop = 0;
      setScrollPosition({ x: 0, y: 0 });
    }
  }, [zoomLevel]);

  // カスタムローディングコンポーネント
  const LoadingSpinner = () => (
    <div className="loading-spinner">
      <span>読み込み中...</span>
    </div>
  );

  // 戻るハンドラー
  const handleFeedbackBack = () => {
    setCurrentPopup('feedback');
  };

  // アイコンコンポーネント
  const BuildingIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
      <line x1="3" y1="6" x2="21" y2="6"/>
      <path d="M16 10a4 4 0 0 1-8 0"/>
    </svg>
  );

  const TagIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
      <line x1="7" y1="7" x2="7.01" y2="7"/>
    </svg>
  );

  const CalendarIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  );

  const FileIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14,2 14,8 20,8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10,9 9,9 8,9"/>
    </svg>
  );

  const ChevronDownIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  );

  return (
    <div className="insurance-detail-page" style={{ overflow: 'hidden', height: '100vh', position: 'relative' }}>

      <div className={`detail-container ${isSidebarOpen ? 'sidebar-open' : ''}`} style={{ overflow: 'hidden', height: '100%' }}>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            {/* フル幅ツールバー（エラー時は非表示） */}
            {!showErrorPage && (
              <>
                <div className="pdf-toolbar">
                  {/* 左セクション：文書情報・ズーム・ページナビ・モバイル虫眼鏡 */}
                  <div className="toolbar-section toolbar-section--left">
                    <div className="pdf-control-group doc-info-toolbar">
                      {isMobile ? (
                        <div className="doc-info-accordion-toolbar">
                          <button
                            className="doc-info-accordion-header-toolbar"
                            onClick={toggleDocInfoAccordion}
                          >
                            <span className={`doc-info-accordion-icon-toolbar ${isDocInfoAccordionOpen ? 'open' : ''}`}>
                              <ChevronDownIcon />
                            </span>
                            <span className="doc-info-accordion-title-toolbar">{documentInfo.insuranceCompany}</span>
                          </button>
                          <div className={`doc-info-accordion-content-toolbar ${isDocInfoAccordionOpen ? 'open' : ''}`}>
                            <div className="doc-info-items-toolbar">
                              <div className="doc-info-item-toolbar doc-info-item-toolbar--full">
                                <img src={iconFiles} alt="ファイルアイコン" className="icon user-icon" />
                                <span className="doc-info-value-toolbar doc-info-text doc-info-text--full">{documentInfo.productName}</span>
                              </div>
                              <div className="doc-info-item-toolbar-row">
                                <div className="doc-info-item-toolbar doc-info-item-toolbar--half">
                                  <span className="doc-info-value-toolbar doc-info-text doc-info-text--10">{documentInfo.date}</span>
                                </div>
                                <div className="doc-info-item-toolbar doc-info-item-toolbar--half">
                                  <img src={iconPages} alt="ファイルアイコン" className="icon user-icon" />
                                  <span className="doc-info-value-toolbar doc-info-text doc-info-text--10">{documentInfo.relatedPage}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="doc-info-table-toolbar">
                          <table className="doc-info-table">
                            <thead>
                              <tr>
                                <th>保険会社名</th>
                                <th>商品名</th>
                                <th>日付</th>
                                <th>関連ページ</th>
                              </tr>
                            </thead>
                            <tbody>
  <tr>
    <td className='character-controls character-controls--5'>
      {documentInfo.insuranceCompany}
    </td>
    <td className='character-controls character-controls--14'>
      {documentInfo.productName}
    </td>
    <td className='character-controls character-controls--5'>
      {documentInfo.date}
    </td>
    <td>{documentInfo.relatedPage}</td>
  </tr>
</tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* ズーム: デスクトップのみ左に表示 */}
                    {!isMobile && (
                      <div className="pdf-control-group left">
                        <button className="pdf-control-button" onClick={handleZoomIn} title="拡大">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            <line x1="11" y1="8" x2="11" y2="14"></line>
                            <line x1="8" y1="11" x2="14" y2="11"></line>
                          </svg>
                        </button>
                        <button className="pdf-control-button" onClick={handleZoomOut} title="縮小">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            <line x1="8" y1="11" x2="14" y2="11"></line>
                          </svg>
                        </button>
                        <span className="zoom-level">{zoomLevel}%</span>
                      </div>
                    )}

                    {/* ページング: デスクトップのみ左に表示 */}
                    {!isMobile && (
                      <div className="pdf-control-group center">
                        <button className="page-nav-button" onClick={handlePrevPage} disabled={currentPage <= 1 || isRendering}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6"></polyline>
                          </svg>
                        </button>
                        <div className="page-indicator">
                          <input
                            type="text"
                            value={currentPage}
                            onChange={(e) => {
                              const val = parseInt(e.target.value);
                              if (!isNaN(val) && !isRendering) {
                                setCurrentPage(val);
                              }
                            }}
                            onBlur={() => {
                              if (currentPage < 1) setCurrentPage(1);
                              if (currentPage > totalPages) setCurrentPage(totalPages);
                            }}
                            className="page-input"
                            disabled={isRendering}
                          />
                          <span className="page-separator">/</span>
                          <span className="total-pages">{totalPages}</span>
                        </div>
                        <button className="page-nav-button" onClick={handleNextPage} disabled={currentPage >= totalPages || isRendering}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 18 15 12 9 6"></polyline>
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* 右セクション：モバイル時ズーム・ページング + 検索 + 閉じるボタン */}
                  <div className="toolbar-section toolbar-section--right">
                    {/* ズーム: モバイルのみ右に表示 */}
                    {isMobile && (
                      <div className="pdf-control-group left">
                        <button className="pdf-control-button" onClick={handleZoomIn} title="拡大">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            <line x1="11" y1="8" x2="11" y2="14"></line>
                            <line x1="8" y1="11" x2="14" y2="11"></line>
                          </svg>
                        </button>
                        <button className="pdf-control-button" onClick={handleZoomOut} title="縮小">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            <line x1="8" y1="11" x2="14" y2="11"></line>
                          </svg>
                        </button>
                        <span className="zoom-level">{zoomLevel}%</span>
                      </div>
                    )}

                    {/* ページング: モバイルのみ右に表示（768px未満でCSS非表示） */}
                    {isMobile && (
                      <div className="pdf-control-group center">
                        <button className="page-nav-button" onClick={handlePrevPage} disabled={currentPage <= 1 || isRendering}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6"></polyline>
                          </svg>
                        </button>
                        <div className="page-indicator">
                          <input
                            type="text"
                            value={currentPage}
                            onChange={(e) => {
                              const val = parseInt(e.target.value);
                              if (!isNaN(val) && !isRendering) {
                                setCurrentPage(val);
                              }
                            }}
                            onBlur={() => {
                              if (currentPage < 1) setCurrentPage(1);
                              if (currentPage > totalPages) setCurrentPage(totalPages);
                            }}
                            className="page-input"
                            disabled={isRendering}
                          />
                          <span className="page-separator">/</span>
                          <span className="total-pages">{totalPages}</span>
                        </div>
                        <button className="page-nav-button" onClick={handleNextPage} disabled={currentPage >= totalPages || isRendering}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 18 15 12 9 6"></polyline>
                          </svg>
                        </button>
                      </div>
                    )}

                    {/* PCレイアウト時: キーワード検索パネルを表示 */}
                    {!isMobile && (
                      <div className="pdf-search-toolbar-panel">
                        <PdfSearchPanel {...searchControlProps} />
                      </div>
                    )}
                    {/* モバイルレイアウト時: キーワード検索ボタン表示 */}
                    {isMobile && (
                      <PdfSearchPanel.Trigger
                        isSearchOpen={isSearchOpen}
                        onToggleSearchOpen={() => setIsSearchOpen(prev => !prev)}
                      />
                    )}

                    {/* 閉じるボタン */}
                    <button onClick={handleClose} className="detail-close-button" title="閉じる">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                </div>

                {/* モバイルレイアウト時: キーワード検索パネルをアコーディオン表示（ツールバー直下） */}
                {isMobile && (
                  <div className={`pdf-search-accordion ${isSearchOpen ? 'open' : ''}`}>
                    <div className="pdf-search-accordion-inner">
                      <PdfSearchPanel {...searchControlProps} />
                    </div>
                  </div>
                )}
              </>
            )}

            {/* コンテンツエリア（PDF + サイドバー横並び） */}
            <div className={`detail-content-area ${isLandscape ? 'landscape-layout' : 'portrait-layout'}`}>
              <div className="pdf-viewer-area">
                {showErrorPage ? (
                  <div className="error-page">
                    <h1 className="error-title">エラーが発生しました</h1>
                    <p className="error-message">
                      {errorMessage || 'PDFの読み込みに失敗しました。'}
                    </p>
                    <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                      <button
                        onClick={handleRetry}
                        style={{ padding: '8px 16px', background: '#e8944a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        再試行
                      </button>
                      <button
                        onClick={handleBackToSearch}
                        style={{ padding: '8px 16px', background: '#4a85e8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        検索画面に戻る
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="pdf-frame-container"
                    ref={containerRef}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseLeave}
                    style={{
                      overflow: 'auto',
                      cursor: isDragging ? 'grabbing' : 'grab',
                      width: '100%',
                      height: '100%',
                      position: 'relative'
                    }}
                  >
                    <div
                      className="pdf-content"
                      style={{
                        userSelect: 'none',
                        padding: '20px',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                        minHeight: '100%'
                      }}
                    >
                      {pdfFile && (
                        <Document
                          file={pdfFile}
                          onLoadSuccess={onDocumentLoadSuccess}
                          onLoadError={onDocumentLoadError}
                          onLoadProgress={({ loaded, total }) => {
                            // 読み込み進捗（必要に応じて進捗バーなどに使用可能）
                          }}
                          loading={<LoadingSpinner />}
                          error={null}
                          options={pdfOptions}
                        >
                          <Page
                            width={isLandscape ? PDF_WIDTH_LANDSCAPE : PDF_WIDTH_PORTAIT}
                            pageNumber={currentPage}
                            onLoadSuccess={onPageLoadSuccess}
                            onLoadError={onPageLoadError}
                            onRenderError={(err) => {
                              // レンダリングエラーは内部で処理
                            }}
                            loading={<LoadingSpinner />}
                            scale={zoomLevel / 100}
                            renderTextLayer={true}
                            customTextRenderer={customTextRenderer}
                            onGetTextSuccess={onGetTextSuccess}
                            onRenderTextLayerSuccess={onTextLayerRenderSuccess}
                            renderAnnotationLayer={false}
                            style={{
                              borderRadius: '8px',
                              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                              opacity: isRendering ? 0.7 : 1,
                              transition: 'opacity 0.2s ease'
                            }}
                          />
                        </Document>
                      )}
                      {isRendering && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          background: 'rgba(255, 255, 255, 0.8)',
                          padding: '10px',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}>
                          レンダリング中...
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="detail-sidebar">
                <div className="search-box-container">
                  <h2 className="search-title">要約</h2>
                  <div className="summary-container">
                    <div className="summary-content">
                      {summary || "要約情報がありません。"}
                    </div>
                  </div>
                </div>
                {/* ガード文言 - 要約の下 */}
                <div className="guard-notice">
                  <div className="guard-notice-content">
                    <svg className="guard-notice-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/>
                      <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                    <span className="guard-notice-text">
                      本サービスは、関連性が高いとAIが判断した文書のページの表示および要約を自動生成しておりますが、<br />
                      正確な取扱いや判断については、各保険会社へご確認ください。
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {currentPopup === 'feedback' && (
        <FeedbackPopup 
          onClose={handleFeedbackClose}
          handleGoodSubmit={handleGoodFeedback}
          handleBadSubmit={handleBadFeedback}
          isSubmitting={isFeedbackSubmitting}
        />
      )}

      {currentPopup === 'badReason' && (
        <BadReasonFeedbackPopup
          onClose={handleFeedbackClose}
          onBack={handleFeedbackBack}
          queryId={currentQueryId}
          handleSubmit={handleFeedbackSubmit}
          isSubmitting={isFeedbackSubmitting}
        />
      )}
    </div>
  );
};

export default InsuranceSystemDetail;
